/**
 * `/browse/albums` — the ALBUMS tab.
 *
 * WHY IT EXISTS. Browse only ever reached albums THROUGH something else: an
 * artist, or a genre. An album whose artist the user could not remember was
 * unreachable from browse at all — findable only by typing the right thing into
 * search. This is the flat A-Z-by-artist view of the whole catalog.
 *
 * It is deliberately a composite of two existing surfaces rather than a new
 * pattern: the tab strip + count line of ../genres/GenresList.js, and the paged
 * `AlbumGridCard` grid (with its targeted ♥ repaint) of ../genres/GenrePage.js.
 * Both the card and the 24-per-page window are IMPORTED from ./ArtistDetail.js,
 * so a change to the album tile lands on all three grids at once.
 *
 * ── ORDER IS THE SERVER'S ─────────────────────────────────────────────────
 * `GET /api/albums` sorts artist-then-year-ascending with no-year albums last
 * (the same tuple the genre page uses — `services/catalog.ts#listAlbums`). The
 * client does NOT re-sort, for the reason ArtistDetail.js documents: the order
 * is global across keyset pages, so sorting each arriving page locally would
 * interleave page 2's early entries above page 1's late ones.
 *
 * @module
 */

/* global AbortController -- browser platform API; eslint.config.js's browser
   globals block predates the views using it */

import { TESTIDS } from '@streamymusic/shared';

import { VirtualList } from '../../components/VirtualList.js';
import { EmptyBlock, ErrorBlock, LIBRARY_PREPARING_MESSAGE, LoadingBlock } from '../../components/StateBlocks.js';
import { FavoriteMarker } from '../../components/primitives/Card.js';
import { append, el } from '../../components/primitives/dom.js';

import { ALBUM_GRID_PAGE_SIZE, AlbumGridCard } from './ArtistDetail.js';
import { BrowseTabs } from './BrowseTabs.js';

/**
 * Builds the `/browse/albums` section and starts loading its first page.
 *
 * @param {{
 *   api: {
 *     getAlbums: (
 *       options?: import('../../../api/api-client.js').PageOptions,
 *     ) => Promise<import('@streamymusic/shared').AlbumListResponse>,
 *   },
 *   heading: HTMLElement,
 *   getState: () => import('../../app/state-store.js').AppState,
 *   subscribe: (listener: (next: import('../../app/state-store.js').AppState) => void) => () => void,
 *   isAdmin?: boolean,
 *   offline?: boolean,
 *   downloadedAlbums?: readonly string[],
 *   onAlbumMenu?: (album: import('@streamymusic/shared').AlbumCard) => void,
 *   pageSize?: number,
 *   depth?: import('../../../lib/list-depth.js').ListDepth,
 *   announce?: (text: string) => void,
 *   warn?: (message: string, detail: Record<string, string>) => void,
 * }} options
 * @returns {{ element: HTMLElement, dispose: () => void }}
 */
export function AlbumsList(options) {
  const {
    api,
    heading,
    getState,
    subscribe,
    isAdmin = false,
    offline = false,
    downloadedAlbums = [],
    onAlbumMenu,
    pageSize = ALBUM_GRID_PAGE_SIZE,
    depth,
    announce,
    warn,
  } = options;

  const section = el('section', { attrs: { 'data-screen-label': 'Browse' } });
  const tabs = BrowseTabs('albums');
  const countLabel = el('p', { className: 'sm-count-label', attrs: { hidden: true } });
  const body = el('div');
  /** Holds an error for a FAILED "load more" — the loaded cards must survive it. */
  const notice = el('div');
  append(section, heading, tabs, countLabel, body, notice);

  const downloads = new Set(downloadedAlbums);

  /** @type {import('@streamymusic/shared').AlbumCard[]} */
  let items = [];
  /** @type {string|null} */
  let cursor = null;
  let generation = 0;
  let disposed = false;
  /** @type {AbortController|null} */
  let controller = null;
  /** Pages held, so the depth memory can replay them on return (#back). */
  let pagesLoaded = 0;

  /** The depth this route was left at, read ONCE before the first paint. */
  const restored = depth?.read() ?? null;

  /**
   * Mounted cards keyed by album id with the ♥ state they were painted with —
   * the same targeted-repaint mechanism as GenrePage's `cardsByAlbumId`.
   *
   * @type {Map<string, { root: HTMLElement, favorited: boolean }>}
   */
  const cardsByAlbumId = new Map();

  const list = VirtualList({
    items,
    windowSize: pageSize,
    renderItem: (album) => {
      const favorited = getState().catalog.favorites.albums.includes(album.id);
      const root = AlbumGridCard({
        album,
        surface: 'browse',
        favorite: favorited,
        offline,
        downloaded: downloads.has(album.id),
        ...(warn === undefined ? {} : { onContractViolation: warn }),
        ...(onAlbumMenu === undefined ? {} : { onMenu: onAlbumMenu }),
      });
      cardsByAlbumId.set(album.id, { root, favorited });
      return root;
    },
    loadMoreLabel: 'Load more ↓',
    // Same derived id as the other two album grids; they live on different
    // routes and can never share a DOM (see ArtistDetail.js's note).
    loadMoreTestId: TESTIDS.LOAD_MORE_BUTTON,
    listClassName: 'sm-grid-covers',
    ...(restored === null ? {} : { initialShown: restored.shown }),
    onWindowChange: (shown) => depth?.remember(pagesLoaded, shown),
    onLoadMore: () => {
      void load({ append: true });
    },
  });

  /** The favourites array the cards were last painted against (identity). */
  let paintedFavAlbums = getState().catalog.favorites.albums;

  /** Toggle ♥ on only the cards whose state flipped (GenrePage's C14 idiom). */
  function syncFromStore() {
    const favAlbums = getState().catalog.favorites.albums;
    if (favAlbums === paintedFavAlbums) return;
    paintedFavAlbums = favAlbums;
    const favoriteSet = new Set(favAlbums);
    for (const [albumId, entry] of cardsByAlbumId) {
      const favorited = favoriteSet.has(albumId);
      if (favorited === entry.favorited || !entry.root.isConnected) continue;
      entry.favorited = favorited;
      const marker = entry.root.querySelector('.sm-cover__fav');
      if (favorited && marker === null) append(entry.root, FavoriteMarker());
      else if (!favorited && marker !== null) marker.remove();
    }
  }

  const unsubscribe = subscribe(() => {
    if (disposed) return;
    syncFromStore();
  });

  function paintCount() {
    const n = items.length;
    countLabel.textContent = `${n.toLocaleString()}${cursor === null ? '' : '+'} album${n === 1 ? '' : 's'}`;
    countLabel.removeAttribute('hidden');
  }

  /**
   * @param {{ append?: boolean }} [opts]
   * @returns {Promise<void>}
   */
  async function load(opts = {}) {
    const appendPage = opts.append === true;
    const own = (generation += 1);
    controller?.abort();
    const ownController = new AbortController();
    controller = ownController;

    notice.replaceChildren();
    if (!appendPage) {
      items = [];
      cursor = null;
      pagesLoaded = 0;
      cardsByAlbumId.clear();
      countLabel.setAttribute('hidden', '');
      body.replaceChildren(LoadingBlock({ count: 4, label: 'Loading albums', variant: 'cover' }));
    }

    try {
      const page = await api.getAlbums({
        limit: pageSize,
        ...(appendPage && cursor !== null ? { cursor } : {}),
        signal: ownController.signal,
      });
      if (disposed || own !== generation) return;

      // Server order preserved verbatim — see the module header.
      items = appendPage ? [...items, ...page.items] : [...page.items];
      cursor = page.nextCursor;
      pagesLoaded += 1;

      if (items.length === 0) {
        // An empty catalog is the library-still-syncing case, not an error —
        // same copy (and admin sync bridge) as the artists/genres tabs.
        body.replaceChildren(
          EmptyBlock({
            message: LIBRARY_PREPARING_MESSAGE,
            showSyncLink: isAdmin,
            ...(announce === undefined ? {} : { announce }),
          }),
        );
        countLabel.setAttribute('hidden', '');
        return;
      }

      paintCount();
      paintedFavAlbums = getState().catalog.favorites.albums;
      list.update({ items, hasMore: cursor !== null });
      if (body.firstChild !== list.element) body.replaceChildren(list.element);
      depth?.remember(pagesLoaded, list.shownCount());
    } catch {
      if (disposed || own !== generation) return;
      const retry = ErrorBlock({
        onRetry: () => {
          void load(appendPage ? { append: true } : {});
        },
        ...(announce === undefined ? {} : { announce }),
      });
      if (appendPage) {
        // A failed NEXT page must not discard the cards already on screen.
        list.update({ busy: false });
        notice.replaceChildren(retry);
      } else {
        body.replaceChildren(retry);
      }
    }
  }

  /**
   * First paint, re-opening the depth the user left (#back). Every replayed page
   * is a hit on the api-client's 24h per-cursor catalog cache — see
   * `lib/list-depth.ts`.
   *
   * @returns {Promise<void>}
   */
  async function loadToRememberedDepth() {
    await load();
    if (restored === null) return;
    for (let page = 1; page < restored.pages; page += 1) {
      if (disposed || cursor === null) return;
      const before = items.length;
      await load({ append: true });
      if (items.length === before) return; // page failed or added nothing
    }
  }

  void loadToRememberedDepth();

  return {
    element: section,
    dispose() {
      disposed = true;
      controller?.abort();
      unsubscribe();
      list.dispose();
    },
  };
}
